import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { sanitizeAgreementHtml } from '../../utils/sanitize-agreement-html'

interface AgreementContentViewProps {
    content: string
    minHeight?: number
    maxHeight?: number
    footer?: React.ReactNode
    onReachedBottom?: () => void
}

// Px tolerance so we don't require hitting the exact last pixel to count as "bottom".
const SCROLL_BOTTOM_THRESHOLD = 8

// Read-only agreement content is stored HTML. There is no need to spin up the
// BlockNote editor to display it: render the sanitized HTML directly. DOMPurify
// guards against anything unexpected in stored content.
const AgreementContentView: React.FC<AgreementContentViewProps> = ({
    content,
    minHeight = 100,
    maxHeight,
    footer,
    onReachedBottom
}) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const sanitized = useMemo(() => sanitizeAgreementHtml(content), [content])

    const notifyIfAtBottom = useCallback(() => {
        const el = scrollRef.current
        if (!el || !onReachedBottom) return
        // Also true when content fits (not scrollable), so a short document
        // doesn't leave the consumer waiting for a scroll that never happens.
        if (el.scrollHeight - el.clientHeight - el.scrollTop <= SCROLL_BOTTOM_THRESHOLD) {
            onReachedBottom()
        }
    }, [onReachedBottom])

    // Re-check when the content's height settles (async render) or changes.
    useEffect(() => {
        const contentEl = contentRef.current
        if (!contentEl || !onReachedBottom) return

        const observer = new ResizeObserver(() => notifyIfAtBottom())
        observer.observe(contentEl)

        return () => observer.disconnect()
    }, [onReachedBottom, notifyIfAtBottom])

    return (
        <div className="text-editor text-editor-readonly border rounded overflow-hidden">
            <div
                ref={scrollRef}
                className="text-editor-scroll"
                // When the content scrolls (maxHeight set), make it focusable so
                // keyboard users can tab in and scroll with the arrow keys.
                tabIndex={maxHeight ? 0 : undefined}
                role={maxHeight ? 'region' : undefined}
                aria-label={maxHeight ? 'Agreement content' : undefined}
                style={{
                    minHeight,
                    padding: '16px',
                    ...(maxHeight ? { maxHeight, overflowY: 'auto' } : {})
                }}
                onScroll={onReachedBottom ? notifyIfAtBottom : undefined}
            >
                <div ref={contentRef}>
                    <div
                        className="agreement-content"
                        dangerouslySetInnerHTML={{ __html: sanitized }}
                    />
                    {footer}
                </div>
            </div>
        </div>
    )
}

AgreementContentView.displayName = 'AgreementContentView'
export { AgreementContentView }
